import { deleteBlockedDetailsSchema, type EnumOption } from '@pmai/contracts';
import { useTranslation } from 'react-i18next';
import { ApiError, type ClientErrorCode } from './api/client';

export type ErrorOverrides = Partial<Record<ClientErrorCode, string>>;

/**
 * One translation of API errors for the whole app: `errors.<CODE>`, or a page-specific text for
 * a code where the page knows more (e.g. CONFLICT on invite = "already a member").
 */
export function useErrorText() {
  const { t } = useTranslation();
  return (error: unknown, overrides: ErrorOverrides = {}): string => {
    const code: ClientErrorCode = error instanceof ApiError ? error.code : 'INTERNAL';
    if (overrides[code]) return overrides[code];
    // FR-WS-012: say what is in the way — "… 2 uzdevumi, 1 piezīme".
    const blocked =
      code === 'DELETE_BLOCKED' && deleteBlockedDetailsSchema.safeParse((error as ApiError).details);
    if (blocked && blocked.success) {
      const what = blocked.data.related.map((r) => t(`related.${r.entity}`, { count: r.count })).join(', ');
      return t('errors.DELETE_BLOCKED_WITH', { what });
    }
    // Jira's own words help (unknown project key, missing permission …).
    if (code === 'JIRA_FAILED' && error instanceof ApiError && error.message)
      return t('errors.JIRA_FAILED_WITH', { message: error.message });
    return t(`errors.${code}`);
  };
}

/** Translated label of an enum option from `@pmai/contracts` (roles, statuses, …). */
export function useEnumLabel() {
  const { t } = useTranslation();
  return <C extends string>(option: EnumOption<C>): string => t(option.labelKey);
}
